'use client';

import { DEFAULT_FEATURE_TITLE, FEATURES } from '@content/home/features';
import { AnimatePresence, motion } from 'motion/react';

import Link from 'next/link';
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { transition as motionTransition } from '@/components/motion/tokens';
import Button from '@/components/ui/Button';
import Eyebrow from '@/components/ui/Eyebrow';
import MuxVideo from '@/components/ui/MuxVideo';
import Pill from '@/components/ui/Pill';
import { track } from '@/lib/analytics/events';
import type { TWithId } from '@/lib/interfaces/shared';
import Tag from '@/ui/Tag';
import { ICONS, PILL_ICONS } from './Icons';

const hasMedia = (feature: (typeof FEATURES)[number] | null) => {
  if (!feature) return false;
  return !!(feature as any).muxPlaybackId || !!feature.image;
};

const Features: FC<TWithId> = ({ id }) => {
  const featuresWithMedia = useMemo(() => FEATURES.filter(hasMedia), []);
  const shouldEnableSelection = featuresWithMedia.length >= 2;
  const homeSpotlightFeature = useMemo(
    () => FEATURES.find((feature) => feature.isHomeSpotlight && hasMedia(feature)) ?? null,
    [],
  );

  // Use first feature with media as default
  const defaultTitle = useMemo(() => {
    const firstWithMedia = homeSpotlightFeature ?? FEATURES.find(hasMedia);
    return firstWithMedia?.title ?? DEFAULT_FEATURE_TITLE;
  }, [homeSpotlightFeature]);

  const [selectedTitle, setSelectedTitle] = useState<string>(defaultTitle);
  const [isDesktop, setIsDesktop] = useState<boolean>(false);
  const [isExpandedMobile, setIsExpandedMobile] = useState<boolean>(false);
  const showMoreButtonRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<number | null>(null);
  const previousExpandedRef = useRef<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 640px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    if (mq.addEventListener) {
      mq.addEventListener('change', update);
    } else {
      mq.addListener(update);
    }
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', update);
      } else {
        mq.removeListener(update);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.sessionStorage.getItem('features-mobile-expanded');
    if (stored === 'true') {
      setIsExpandedMobile(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem('features-mobile-expanded', String(isExpandedMobile));
  }, [isExpandedMobile]);

  const handleSelect = (title: string) => {
    if (!isDesktop || !shouldEnableSelection) return;
    setSelectedTitle(title);
    const feature = FEATURES.find((f) => f.title === title);
    if (feature?.key && feature.title) {
      track.feature({
        feature_id: feature.key,
        feature_title: feature.title,
        section_id: typeof id === 'undefined' ? undefined : String(id),
        section_title: 'Features',
      });
    }
  };

  const selectedFeature = useMemo(() => {
    const found = FEATURES.find((f) => f.title === selectedTitle);
    if (found) return found;
    return DEFAULT_FEATURE_TITLE ? (FEATURES.find((f) => f.title === DEFAULT_FEATURE_TITLE) ?? null) : null;
  }, [selectedTitle]);

  // Mobile-only: show first 4, reveal others on demand (original behavior)
  const MOBILE_COUNT = 4;
  const baseMobileFeatures = useMemo(() => FEATURES.slice(0, MOBILE_COUNT), []);
  const extraMobileFeatures = useMemo(() => FEATURES.slice(MOBILE_COUNT), []);
  const showMoreVisible = !isDesktop && !isExpandedMobile && extraMobileFeatures.length > 0;

  const muxPlaybackId = (selectedFeature as any)?.muxPlaybackId as string | undefined;
  const muxPosterSecond = selectedFeature?.posterSecond ?? 10;

  const scrollShowMoreButtonIntoView = useCallback(() => {
    const button = showMoreButtonRef.current;
    if (!button) return;

    const { top, height } = button.getBoundingClientRect();
    const target = window.scrollY + top - window.innerHeight / 2 + height / 2;
    window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, []);

  const clearScrollTimeout = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  const toggleMobileFeatures = (expand: boolean) => {
    setIsExpandedMobile(expand);
  };

  useEffect(() => {
    const wasExpanded = previousExpandedRef.current;
    previousExpandedRef.current = isExpandedMobile;

    if (isDesktop) {
      clearScrollTimeout();
      return;
    }

    const collapsedFromExpanded = wasExpanded && !isExpandedMobile;
    if (!collapsedFromExpanded) {
      clearScrollTimeout();
      return;
    }

    clearScrollTimeout();
    scrollTimeoutRef.current = window.setTimeout(() => {
      scrollShowMoreButtonIntoView();
      scrollTimeoutRef.current = null;
    }, 400);

    return () => {
      clearScrollTimeout();
    };
  }, [isExpandedMobile, isDesktop, clearScrollTimeout, scrollShowMoreButtonIntoView]);

  const renderFeatureContent = (feature: (typeof FEATURES)[number]) => {
    const Icon = ICONS[feature.icon];
    const featureHasMedia = hasMedia(feature);
    const isSelectable = shouldEnableSelection && featureHasMedia;
    const featureHref = feature.href;
    const pills =
      feature.pills && feature.pills.length > 0 ? (
        <span className='inline-flex flex-wrap items-center gap-2 sm:ml-auto sm:mt-0 mt-1'>
          {feature.pills
            .filter((pill) => {
              if (pill.icon === 'play-circle') {
                return !!(feature as any).muxPlaybackId || !!feature.href;
              }
              return true;
            })
            .map((pill) => {
              const PillIcon = PILL_ICONS[pill.icon];
              return (
                <Pill key={`${feature.key}-${pill.label}`} leftIcon={<PillIcon className='h-3 w-3' />}>
                  {pill.label}
                </Pill>
              );
            })}
        </span>
      ) : null;

    if (featureHref) {
      return (
        <Link
          href={featureHref}
          className='group block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-info'
          onClick={() =>
            track.nav({
              link_url: featureHref,
              link_text: `feature-${feature.key}`,
              section_id: typeof id === 'undefined' ? undefined : String(id),
              section_title: 'Features',
            })
          }
        >
          <div className='relative transition duration-300 group-hover:-translate-y-[2px]'>
            <span
              aria-hidden='true'
              className='pointer-events-none absolute -inset-[2px] rounded-[calc(0.5rem+2px)] opacity-0 transition duration-300 group-hover:opacity-100 group-focus-visible:opacity-100'
              style={{
                background: 'linear-gradient(135deg, rgba(10,193,141,0.95), rgba(42,107,221,0.95))',
                padding: '2px',
                WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                maskComposite: 'exclude',
              }}
            />
            <div className='relative flex h-full flex-col rounded-lg bg-[#f9fbfc] px-4 py-2 text-sm text-secondary ring-0 ring-offset-0 transition duration-300 dark:bg-white/5 dark:text-white dark:group-hover:bg-white/10'>
              <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 font-semibold text-primary-foreground dark:text-white'>
                <div className='flex items-center gap-3'>
                  <Icon aria-hidden='true' className='h-5 w-5 text-info' />
                  <h3 className='inline'>{feature.title}</h3>
                </div>
                {pills}
              </div>
              <p className='mt-2 text-sm text-secondary-foreground dark:text-white/80'>{feature.description}</p>
              <span className='mt-1 inline-flex items-center self-end text-sm font-semibold text-info'>
                Learn more &rarr;
              </span>
            </div>
          </div>
        </Link>
      );
    }

    return (
      // biome-ignore lint/a11y/useSemanticElements: contains an <h3> which is not valid inside a <button>; keyboard support is provided via onKeyDown
      <div
        role='button'
        tabIndex={isDesktop && isSelectable ? 0 : -1}
        onKeyDown={(e) => {
          if (!isDesktop || !isSelectable) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelectedTitle(feature.title);
          }
        }}
        className={`relative flex flex-col rounded-lg px-4 py-2 transition-none ${
          isSelectable ? 'pointer-events-none sm:pointer-events-auto sm:cursor-pointer' : 'pointer-events-none'
        } ${
          'bg-gray-50 ring-0 ring-offset-0 dark:bg-white/5 ' +
          (isSelectable ? 'sm:hover:bg-gray-100 sm:dark:hover:bg-white/10 ' : '') +
          (selectedTitle === feature.title && isSelectable
            ? 'sm:bg-gray-100 sm:ring-2 sm:ring-info sm:ring-offset-2 sm:ring-offset-primary sm:dark:bg-white/10'
            : '')
        }`}
        onClick={() => isSelectable && handleSelect(feature.title)}
      >
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 font-semibold text-primary-foreground'>
          <div className='flex items-center gap-3'>
            <Icon aria-hidden='true' className='h-5 w-5 text-info' />
            <h3 className='inline'>{feature.title}</h3>
          </div>
          {pills}
        </div>
        <p className='mt-2 text-sm text-secondary-foreground'>{feature.description}</p>
      </div>
    );
  };

  const renderFeature = (feature: (typeof FEATURES)[number], keyPrefix = '') => (
    <div key={`${keyPrefix}${feature.key}`} className={feature.colSpan === 2 ? 'sm:col-span-2' : undefined}>
      {renderFeatureContent(feature)}
    </div>
  );

  return (
    <Tag
      tag='section'
      id={id}
      className='bg-primary py-12 sm:py-24 lg:py-32'
      inMotion
      data-section-title='Features'
      aria-labelledby='features-heading'
    >
      <div className='mx-auto max-w-[90rem] px-6 lg:px-8'>
        <div className='mx-auto grid max-w-2xl grid-cols-1 gap-x-16 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2'>
          <div>
            <Eyebrow size='base' aria-hidden='true'>
              Features
            </Eyebrow>
            <h2
              id='features-heading'
              className='mt-2 text-4xl font-normal tracking-tight text-pretty text-secondary sm:text-5xl'
            >
              Everything you need, at your fingertips
            </h2>
            <p className='mt-6 text-base/7 text-secondary-foreground'>
              Our platform is modular and flexible. Turn on the modules you need today and keep the rest off. As
              operations or customer demands grow, you can enable additional modules in a couple of clicks, with minimal
              setup.
            </p>
            {hasMedia(selectedFeature) ? (
              <div className='mt-10'>
                <div
                  className='relative rounded-xl bg-gray-900/5 p-2 ring-1 ring-gray-900/10 ring-inset lg:rounded-2xl lg:p-4 dark:bg-white/2.5
                      dark:ring-white/10'
                >
                  <div className='relative aspect-video w-full'>
                    {/* Mux video player - mounted directly with poster; no extra overlay */}
                    {muxPlaybackId && selectedFeature && (selectedFeature as any).muxPlaybackId ? (
                      <MuxVideo
                        playbackId={muxPlaybackId}
                        posterSecond={muxPosterSecond}
                        title={selectedFeature.title}
                        className='rounded-sm bg-gray-50 shadow-xl ring-1 ring-gray-900/10 dark:bg-white/5 dark:ring-white/10'
                      />
                    ) : null}

                    {/* Images shown when the selected feature has images */}
                    {!(selectedFeature as any)?.muxPlaybackId && (
                      <div>
                        {/* biome-ignore lint/performance/noImgElement: light/dark variants swap via tailwind dark: classes; converting to next/image needs layout restructure */}
                        <img
                          key={`${selectedFeature?.key}-light`}
                          src={selectedFeature?.image}
                          alt={selectedTitle}
                          className='size-full rounded-sm bg-gray-50 object-cover shadow-xl ring-1 ring-gray-900/10 transition-transform duration-300 ease-out hover:scale-105 dark:hidden dark:bg-white/5 dark:ring-white/10'
                          loading='eager'
                        />
                        {/* biome-ignore lint/performance/noImgElement: light/dark variants swap via tailwind dark: classes; converting to next/image needs layout restructure */}
                        <img
                          key={`${selectedFeature?.key}-dark`}
                          src={selectedFeature?.imageDark}
                          alt={selectedTitle}
                          className='hidden size-full rounded-sm bg-gray-50 object-cover shadow-xl ring-1 ring-gray-900/10 transition-transform duration-300 ease-out hover:scale-105 dark:block dark:bg-white/5 dark:ring-white/10'
                          loading='eager'
                        />
                      </div>
                    )}
                  </div>
                </div>
                {selectedFeature?.caption && (
                  <div className='mt-4 flex min-h-[40px] items-center justify-end gap-3'>
                    <p className='text-right text-sm text-pretty text-secondary-foreground/80'>
                      {selectedFeature.caption}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              featuresWithMedia.length === 0 && (
                <div className='mt-10 hidden sm:block'>
                  <div className='relative aspect-video w-full flex items-center justify-center'>
                    <svg
                      viewBox='-40 -15 177 130'
                      fill='none'
                      xmlns='http://www.w3.org/2000/svg'
                      className='w-[120%] h-[100%] max-w-none max-h-none'
                      aria-hidden='true'
                      preserveAspectRatio='xMidYMid meet'
                    >
                      <defs>
                        {/* Green glow filter */}
                        <filter id='glow-green' x='-200%' y='-200%' width='500%' height='500%'>
                          <feGaussianBlur in='SourceGraphic' stdDeviation='6' result='blur' />
                          <feColorMatrix
                            in='blur'
                            type='matrix'
                            values='0 0 0 0 0.039  0 0 0 0 0.757  0 0 0 0 0.553  0 0 0 1 0'
                            result='glow'
                          >
                            <animate
                              attributeName='values'
                              values='0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 0 0;
                                        0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 1.0 0;
                                        0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 0 0;
                                        0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 0 0;
                                        0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 1.0 0;
                                        0 0 0 0 0.039 0 0 0 0 0.757 0 0 0 0 0.553 0 0 0 0 0'
                              dur='9s'
                              repeatCount='indefinite'
                            />
                          </feColorMatrix>
                          <feMerge>
                            <feMergeNode in='SourceGraphic' />
                            <feMergeNode in='glow' />
                          </feMerge>
                        </filter>

                        {/* Blue glow filter */}
                        <filter id='glow-blue' x='-200%' y='-200%' width='500%' height='500%'>
                          <feGaussianBlur in='SourceGraphic' stdDeviation='6' result='blur' />
                          <feColorMatrix
                            in='blur'
                            type='matrix'
                            values='0 0 0 0 0.165  0 0 0 0 0.420  0 0 0 0 0.867  0 0 0 1 0'
                            result='glow'
                          >
                            <animate
                              attributeName='values'
                              values='0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 0 0;
                                        0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 1.0 0;
                                        0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 0 0;
                                        0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 0 0;
                                        0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 1.0 0;
                                        0 0 0 0 0.165 0 0 0 0 0.420 0 0 0 0 0.867 0 0 0 0 0'
                              dur='9s'
                              repeatCount='indefinite'
                            />
                          </feColorMatrix>
                          <feMerge>
                            <feMergeNode in='SourceGraphic' />
                            <feMergeNode in='glow' />
                          </feMerge>
                        </filter>
                      </defs>

                      {/* Element 1: Left blue rectangle - glows first */}
                      <path d='M0 0H22V100H0V0Z' fill='#2A6BDD' filter='url(#glow-blue)' />

                      {/* Element 2: Top green + Center blue shapes - glow second */}
                      <path d='M31 0C43.1503 0 53 9.84974 53 22H31V0Z' fill='#0AC18D' filter='url(#glow-green)' />
                      <path d='M31 31H53V100H31V31Z' fill='#2A6BDD' filter='url(#glow-blue)' />

                      {/* Element 3: Right shapes (top blue + bottom green) - glow third */}
                      <path
                        d='M75.2026 33.6262C71.0168 31.8924 66.5306 31 62 31L62 65.5L86.3952 41.1048C83.1916 37.9012 79.3883 35.3599 75.2026 33.6262Z'
                        fill='#2A6BDD'
                        filter='url(#glow-blue)'
                      />
                      <path
                        d='M93.8738 52.2974C92.1401 48.1117 89.5988 44.3084 86.3952 41.1048L62 65.5L62 100C66.5306 100 71.0168 99.1076 75.2026 97.3738C79.3883 95.6401 83.1916 93.0988 86.3952 89.8952C89.5988 86.6916 92.1401 82.8883 93.8738 78.7026C95.6076 74.5168 96.5 70.0306 96.5 65.5C96.5 60.9694 95.6076 56.4832 93.8738 52.2974Z'
                        fill='#0AC18D'
                        filter='url(#glow-green)'
                      />
                    </svg>
                  </div>
                </div>
              )
            )}
          </div>
          <div className='relative flex h-full flex-col justify-center'>
            <div className='grid grid-cols-1 gap-x-8 gap-y-6 text-base/7 text-secondary-foreground sm:grid-cols-2 lg:gap-y-10'>
              {isDesktop ? (
                FEATURES.map((feature) => renderFeature(feature))
              ) : (
                <>
                  {baseMobileFeatures.map((feature) => renderFeature(feature))}
                  <AnimatePresence initial={false}>
                    {isExpandedMobile &&
                      extraMobileFeatures.map((feature) => (
                        <motion.div
                          key={feature.key}
                          className={feature.colSpan === 2 ? 'sm:col-span-2' : undefined}
                          initial={{ opacity: 0, y: -8, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -8, scale: 0.98 }}
                          transition={{ ...motionTransition, duration: 0.35 }}
                        >
                          {renderFeatureContent(feature)}
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </>
              )}
            </div>
            {showMoreVisible ? (
              <div className='pointer-events-none absolute inset-x-0 bottom-0 sm:hidden'>
                <div className='pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-primary to-transparent' />
                <div
                  ref={showMoreButtonRef}
                  className='pointer-events-auto absolute left-1/2 bottom-0 z-10 -translate-x-1/2 translate-y-1/2'
                >
                  <Button size='sm' variant='primary' onClick={() => toggleMobileFeatures(true)}>
                    Show more
                  </Button>
                </div>
              </div>
            ) : null}
            {!isDesktop && isExpandedMobile ? (
              <div className='mt-4 flex justify-center sm:hidden'>
                <Button size='sm' variant='secondary' onClick={() => toggleMobileFeatures(false)}>
                  Show less
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Tag>
  );
};

export default Features;
